import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Apartment as ApartmentIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProperties } from '../../store/slices/propertySlice';
import { showSuccess, showError, showConfirm, showLoading, showWarning, closeAlert } from '../../utils/sweetAlert';
import adminAPI from '../../services/api/adminAPI';
import { propertyAPI } from '../../services/api/propertyAPI';
import DataTable from '../../components/UI/DataTable';
import TableActions from '../../components/UI/TableActions';
import PageHeader from '../../components/UI/PageHeader';
import AdminPage from '../../components/Admin/AdminPage';
import AdminStatStrip from '../../components/Admin/AdminStatStrip';
import AdminStatusChip from '../../components/Admin/AdminStatusChip';
import { formatMoney } from '../../utils/formatMoney';
import { adminPrimaryButtonSx } from '../../theme/designTokens';

const AdminPropertyOwners = () => {
  const dispatch = useDispatch();
  const { properties } = useSelector(state => state.properties);
  const { user } = useSelector(state => state.auth);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    status: 'active'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ownerToDelete, setOwnerToDelete] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadPropertyOwners();
    }
  }, [user]);

  const loadPropertyOwners = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get all users
      const usersResponse = await adminAPI.getAllUsers();
      const allUsers = Array.isArray(usersResponse) ? usersResponse : [];
      
      // Filter for owners only
      const owners = allUsers.filter(user => user.role === 'owner');
      
      // Get all properties to calculate stats
      const propertiesResponse = await adminAPI.getAllProperties();
      const allProperties = Array.isArray(propertiesResponse) ? propertiesResponse : [];
      
      // Get all rental units
      const unitsResponse = await adminAPI.getAllRentalUnits();
      const allRentalUnits = Array.isArray(unitsResponse) ? unitsResponse : [];
      
      // Process each owner to get their stats
      const ownersData = await Promise.all(owners.map(async (owner) => {
        // Get properties owned by this user
        const ownerProperties = allProperties.filter(prop => prop.owner_id === owner.id);
        
        // Get rental units for owner's properties
        const propertyIds = ownerProperties.map(p => p.id);
        const ownerRentalUnits = allRentalUnits.filter(unit => propertyIds.includes(unit.property_id));
        
        // Calculate stats
        const occupiedUnits = ownerRentalUnits.filter(u => u.status === 'occupied').length;
        const availableUnits = ownerRentalUnits.filter(u => u.status === 'available').length;
        const totalUnits = ownerRentalUnits.length;
        
        // Calculate monthly revenue (sum of occupied units' rent)
        const monthlyRevenue = ownerRentalUnits
          .filter(u => u.status === 'occupied')
          .reduce((sum, unit) => sum + (parseFloat(unit.monthly_rent) || 0), 0);
        
        // Get tenants count (approximate from occupied units)
        const totalTenants = occupiedUnits;
        
        return {
          id: owner.id,
          name: `${owner.first_name} ${owner.last_name}`,
          email: owner.email,
          phone: owner.phone || 'Not provided',
          location: ownerProperties.length > 0 ? ownerProperties[0].location : 'Not specified',
          totalProperties: ownerProperties.length,
          totalUnits: totalUnits,
          occupiedUnits: occupiedUnits,
          availableUnits: availableUnits,
          totalTenants: totalTenants,
          monthlyRevenue: monthlyRevenue,
          growthRate: 0, // Placeholder - would need historical data
          status: owner.is_active ? 'active' : 'inactive',
          joinDate: owner.created_at ? new Date(owner.created_at).toISOString().split('T')[0] : 'N/A',
          lastActivity: owner.updated_at ? new Date(owner.updated_at).toLocaleDateString() : 'N/A',
          properties: ownerProperties.map(prop => ({
            id: prop.id,
            name: prop.name,
            location: prop.location,
            units: totalUnits,
            occupied: occupiedUnits,
            revenue: monthlyRevenue
          }))
        };
      }));
      
      setPropertyOwners(ownersData);
    } catch (err) {
      console.error('Failed to load property owners:', err);
      setError('Failed to load property owners data. Please try again.');
      showError('Load Failed', 'Unable to fetch property owners information.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOwner = (owner) => {
    setSelectedOwner(owner);
    setViewDialogOpen(true);
    setTabValue(0);
  };

  const handleEditOwner = (owner) => {
    setSelectedOwner(owner);
    setEditFormData({
      name: owner.name,
      email: owner.email,
      phone: owner.phone,
      location: owner.location,
      status: owner.status
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedOwner(null);
    setTabValue(0);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedOwner(null);
    setEditFormData({
      name: '',
      email: '',
      phone: '',
      location: '',
      status: 'active'
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    const loadingAlert = showLoading('Updating owner...', 'Please wait');
    
    try {
      // TODO: Implement real API call to update user when backend endpoint is available
      // For now, we'll show a message that editing requires backend implementation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      closeAlert();
      showWarning(
        'Feature Coming Soon', 
        'User editing functionality requires backend implementation. Contact system administrator.'
      );
      handleCloseEditDialog();
      // Reload data to ensure we have the latest
      await loadPropertyOwners();
    } catch (error) {
      closeAlert();
      showError('Update Failed', 'Failed to update owner information.');
      setError('Failed to update owner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDeleteOwner = async (owner) => {
    const result = await showConfirm(
      'Delete Owner?',
      `Are you sure you want to delete ${owner.name}? This action cannot be undone.`,
      'Yes, Delete',
      'Cancel'
    );
    
    if (result.isConfirmed) {
      setSubmitting(true);
      const loadingAlert = showLoading('Deleting owner...', 'Please wait');
      
      try {
        // TODO: Implement real API call to delete user when backend endpoint is available
        // For now, we'll show a message that deletion requires backend implementation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        closeAlert();
        showWarning(
          'Feature Coming Soon', 
          'User deletion functionality requires backend implementation and proper safeguards. Contact system administrator.'
        );
        // Reload data to ensure we have the latest
        await loadPropertyOwners();
      } catch (error) {
        closeAlert();
        showError('Delete Failed', 'Failed to delete owner.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setOwnerToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!ownerToDelete) return;
    
    setSubmitting(true);
    const loadingAlert = showLoading('Deleting owner...', 'Please wait');
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedOwners = propertyOwners.filter(owner => owner.id !== ownerToDelete.id);
      setPropertyOwners(updatedOwners);
      
      closeAlert();
      showSuccess('Owner Deleted!', 'The owner has been successfully removed from the system.');
      handleCloseDeleteDialog();
    } catch (error) {
      closeAlert();
      showError('Delete Failed', 'Failed to delete owner.');
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <AdminPage>
        <Alert severity="error">You need admin privileges to access this page.</Alert>
      </AdminPage>
    );
  }

  const totalProperties = propertyOwners.reduce((sum, owner) => sum + owner.totalProperties, 0);
  const totalUnits = propertyOwners.reduce((sum, owner) => sum + owner.totalUnits, 0);
  const totalPortfolioRent = propertyOwners.reduce((sum, owner) => sum + owner.monthlyRevenue, 0);

  const ownerColumns = [
    {
      id: 'name',
      label: 'Owner',
      getSearchValue: (row) => row.name,
      render: (owner) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2 }}>{owner.name.charAt(0)}</Avatar>
          <Box>
            <Typography variant="subtitle2">{owner.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              Joined: {new Date(owner.joinDate).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'contact',
      label: 'Contact',
      getSearchValue: (row) => `${row.email} ${row.phone}`,
      render: (owner) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">{owner.email}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">{owner.phone}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'location',
      label: 'Location',
      getSearchValue: (row) => row.location,
      render: (owner) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">{owner.location}</Typography>
        </Box>
      ),
    },
    {
      id: 'totalProperties',
      label: 'Properties',
      render: (owner) => (
        <Typography variant="body2" fontWeight="bold">{owner.totalProperties}</Typography>
      ),
    },
    {
      id: 'units',
      label: 'Units',
      render: (owner) => (
        <Box>
          <Typography variant="body2">
            <strong>{owner.occupiedUnits}</strong> / {owner.totalUnits}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {owner.totalUnits ? `${Math.round((owner.occupiedUnits / owner.totalUnits) * 100)}% occupied` : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'totalTenants',
      label: 'Tenants',
      render: (owner) => (
        <Typography variant="body2" fontWeight="bold">{owner.totalTenants}</Typography>
      ),
    },
    {
      id: 'monthlyRevenue',
      label: 'Monthly Revenue',
      render: (owner) => (
        <Typography variant="body2" fontWeight="bold" color="success.main">
          {formatMoney(owner.monthlyRevenue, 'UGX')}
        </Typography>
      ),
    },
    {
      id: 'growthRate',
      label: 'Growth',
      render: (owner) => (
        <Chip
          label={`+${owner.growthRate}%`}
          color={owner.growthRate > 10 ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (owner) => <AdminStatusChip status={owner.status} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (owner) => (
        <TableActions
          actions={[
            { icon: <ViewIcon fontSize="small" />, label: 'View Details', onClick: () => handleViewOwner(owner) },
            { icon: <EditIcon fontSize="small" />, label: 'Edit Owner', onClick: () => handleEditOwner(owner) },
            { icon: <DeleteIcon fontSize="small" />, label: 'Delete Owner', onClick: () => handleDeleteOwner(owner) },
          ]}
        />
      ),
    },
  ];

  return (
    <AdminPage>
      <PageHeader variant="admin" title="Owners" subtitle="Property owner accounts" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <AdminStatStrip
        loading={loading}
        stats={[
          { id: 'owners', title: 'Total owners', value: propertyOwners.length, icon: <BusinessIcon /> },
          { id: 'properties', title: 'Total properties', value: totalProperties, icon: <HomeIcon /> },
          { id: 'units', title: 'Total units', value: totalUnits, icon: <ApartmentIcon /> },
          {
            id: 'rent',
            title: 'Portfolio rent (monthly)',
            value: formatMoney(totalPortfolioRent, 'UGX'),
            subtitle: 'Landlord rent inventory',
            icon: <MoneyIcon />,
          },
        ]}
      />

      <DataTable
        columns={ownerColumns}
        rows={propertyOwners}
        loading={loading}
        title="Property Owners Overview"
        subtitle="Owners, properties, and revenue metrics"
        emptyTitle="No property owners found"
        emptyDescription="Property owners will appear here once they register and add properties."
        emptyIcon={BusinessIcon}
        searchPlaceholder="Search by name, email, or location…"
      />


      {/* View Owner Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          Owner Details - {selectedOwner?.name}
        </DialogTitle>
        <DialogContent>
          {selectedOwner && (
            <Box>
              <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label="Overview" />
                <Tab label="Properties" />
                <Tab label="Financials" />
              </Tabs>

              {tabValue === 0 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">{selectedOwner.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{selectedOwner.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{selectedOwner.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                      <Typography variant="body1">{selectedOwner.location}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip 
                        label={selectedOwner.status} 
                        color={selectedOwner.status === 'active' ? "success" : "default"}
                        size="small" 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Join Date</Typography>
                      <Typography variant="body1">{selectedOwner.joinDate}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Properties ({selectedOwner.totalProperties})</Typography>
                  <List>
                    {selectedOwner.properties.map((property, index) => (
                      <ListItem key={index} divider>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.light' }}>
                            <HomeIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={property.name}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {property.location}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {property.occupied}/{property.units} units occupied
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatMoney(property.revenue, 'UGX')}/month revenue
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Total Units</Typography>
                          <Typography variant="h4" color="primary.main">{selectedOwner.totalUnits}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Occupied Units</Typography>
                          <Typography variant="h4" color="success.main">{selectedOwner.occupiedUnits}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Monthly Revenue</Typography>
                          <Typography variant="h4" color="success.main">
                            {formatMoney(selectedOwner.monthlyRevenue, 'UGX')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Growth Rate</Typography>
                          <Typography variant="h4" color={selectedOwner.growthRate > 0 ? "success.main" : "error.main"}>
                            {selectedOwner.growthRate > 0 ? '+' : ''}{selectedOwner.growthRate}%
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Owner Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          Edit Owner - {selectedOwner?.name}
        </DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Updating...
                </>
              ) : (
                'Update Owner'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete Owner
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{ownerToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. All associated data will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default AdminPropertyOwners;
